// Calls into the solver core running in a Web Worker. Every call is JSON in, JSON out.
// Two lanes: the main one for quick answers (checks, nudges, the builder's live report) and an
// assist lane for the builder's slower help (suggestions, break-ins, Tidy), so one never waits
// behind the other.
import type {
  Assist,
  AssistRequest,
  BuildReport,
  InputMode,
  NudgeAnswer,
  PuzzleDoc,
  Rc,
  Tidied,
  TidyRequest,
} from '@zinc/shared';

type CoreFn =
  | 'analyze'
  | 'check'
  | 'true_candidates'
  | 'export_link'
  | 'import_puzzle'
  | 'solver_version'
  | 'nudge'
  | 'builder_report'
  | 'builder_suggest'
  | 'builder_break_in'
  | 'builder_tidy'
  | 'rules_text'
  | 'rule_list';

export type Lane = 'main' | 'assist';

interface Pending {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
}

const workers = new Map<Lane, Worker>();
let nextId = 1;
const pending = new Map<number, Pending>();

function getWorker(lane: Lane): Worker {
  const existing = workers.get(lane);
  if (existing) return existing;
  const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = (event: MessageEvent<{ id: number; out: string }>) => {
    const p = pending.get(event.data.id);
    if (!p) return;
    pending.delete(event.data.id);
    const parsed = JSON.parse(event.data.out) as unknown;
    if (parsed && typeof parsed === 'object' && 'error' in parsed) {
      p.reject(new Error(String(parsed.error)));
    } else {
      p.resolve(parsed);
    }
  };
  workers.set(lane, worker);
  return worker;
}

export function coreAvailable(): boolean {
  return typeof Worker !== 'undefined';
}

export function callCore<T>(fn: CoreFn, arg: unknown, lane: Lane = 'main'): Promise<T> {
  if (!coreAvailable()) return Promise.reject(new Error('Web Workers are not available'));
  const id = nextId++;
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
    getWorker(lane).postMessage({
      id,
      fn,
      arg: typeof arg === 'string' ? arg : JSON.stringify(arg),
    });
  });
}

export interface Conflict {
  cells: Rc[];
  rule: string;
}

export interface NudgeRequest {
  doc: PuzzleDoc;
  /** One symbol or null per cell, in core order. */
  values: (string | null)[];
  centre: string[][];
  corner: string[][];
  mode: InputMode;
  /** Cells of the latest moves, most recent first. */
  recent: number[];
}

export const core = {
  nudge: (req: NudgeRequest) => callCore<NudgeAnswer>('nudge', req),
  check: (doc: PuzzleDoc, values: (string | null)[]) =>
    callCore<Conflict[]>('check', { doc, values }),
  exportLink: (doc: PuzzleDoc) => callCore<string>('export_link', doc),
  builderReport: (doc: PuzzleDoc, candidates: boolean) =>
    callCore<BuildReport>('builder_report', { doc, candidates }),
  suggest: (req: AssistRequest) => callCore<Assist>('builder_suggest', req, 'assist'),
  breakIn: (req: AssistRequest) => callCore<Assist>('builder_break_in', req, 'assist'),
  tidy: (req: TidyRequest) => callCore<Tidied>('builder_tidy', req, 'assist'),
};
